/**
 * Normalize a phone number by removing non-digit characters
 */
export function normalizePhoneNumber(phone) {
    return phone.replace(/\D/g, '');
}
/**
 * Format a phone number for display
 * Supports US/Canada, Australia, UK, and generic international formats
 */
export function formatPhoneNumber(phone) {
    if (!phone)
        return '-';
    const normalized = normalizePhoneNumber(phone);
    if (normalized.length < 10)
        return phone; // Return original if too short
    // US/Canada: 10 digits (no country code) -> +1 (XXX) XXX-XXXX
    if (normalized.length === 10 && /^[2-9]/.test(normalized)) {
        return `+1 (${normalized.slice(0, 3)}) ${normalized.slice(3, 6)}-${normalized.slice(6)}`;
    }
    // US/Canada: 11 digits starting with 1 -> +1 (XXX) XXX-XXXX
    if (normalized.length === 11 && normalized.startsWith('1')) {
        return `+1 (${normalized.slice(1, 4)}) ${normalized.slice(4, 7)}-${normalized.slice(7)}`;
    }
    // Australia: 11 digits starting with 61 -> +61 X XXXX XXXX
    if (normalized.length === 11 && normalized.startsWith('61')) {
        return `+61 ${normalized.slice(2, 3)} ${normalized.slice(3, 7)} ${normalized.slice(7)}`;
    }
    // Australia: 12 digits starting with 61 -> +61 XX XXXX XXXX
    if (normalized.length === 12 && normalized.startsWith('61')) {
        return `+61 ${normalized.slice(2, 4)} ${normalized.slice(4, 8)} ${normalized.slice(8)}`;
    }
    // UK: 12 digits starting with 44 -> +44 XXXX XXX XXX
    if (normalized.length === 12 && normalized.startsWith('44')) {
        return `+44 ${normalized.slice(2, 6)} ${normalized.slice(6, 9)} ${normalized.slice(9)}`;
    }
    // UK: 13 digits starting with 44 -> +44 XXXXX XXX XXX
    if (normalized.length === 13 && normalized.startsWith('44')) {
        return `+44 ${normalized.slice(2, 7)} ${normalized.slice(7, 10)} ${normalized.slice(10)}`;
    }
    // Generic international: try to detect country code and format nicely
    // 2-digit country codes (most common)
    if (normalized.length >= 11 && normalized.length <= 15) {
        const countryCode = normalized.slice(0, 2);
        const rest = normalized.slice(2);
        // Format remaining digits in groups of 3-4
        const formatted = rest.replace(/(\d{3,4})(?=\d)/g, '$1 ').trim();
        return `+${countryCode} ${formatted}`;
    }
    // Fallback: just add + prefix and space every 4 digits
    return '+' + normalized.replace(/(\d{4})(?=\d)/g, '$1 ');
}
/**
 * Validate a phone number (basic validation)
 */
export function isValidPhoneNumber(phone) {
    const normalized = normalizePhoneNumber(phone);
    return normalized.length >= 10 && normalized.length <= 15;
}
/**
 * Check if a phone number is a US number
 */
export function isUSPhoneNumber(phone) {
    const normalized = normalizePhoneNumber(phone);
    // 10-digit US number
    if (normalized.length === 10) {
        return true;
    }
    // 11-digit US number starting with 1
    if (normalized.length === 11 && normalized.startsWith('1')) {
        return true;
    }
    return false;
}
/**
 * Extract country code from phone number
 */
export function extractCountryCode(phone) {
    const normalized = normalizePhoneNumber(phone);
    if (normalized.length === 11 && normalized.startsWith('1')) {
        return 'US';
    }
    // Add more country code detection as needed
    return null;
}
